import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Plus, Trash2 } from "lucide-react";
import api from "../services/api";

const documentTypeLabels = {
  "purchase-invoices": "Purchase Invoice",
  "sales-orders": "Sales Order",
  "sales-invoices": "Sales Invoice",
  "sales-invoices-pdf": "Sales Invoice (PDF)",
  products: "Product Master",
  customers: "Customer Master",
};

// what a derived field is worked out from when its column is missing from the file
const derivedRuleLabels = {
  TODAY: "Date of the upload",
  PRODUCT_GST: "Product's GST % on the line value",
  PRICE_LIST_PURCHASE_PRICE: "Latest purchase price from the price list",
};

const PDF_KEY = "sales-invoices-pdf";

// where a field's label is in a bill PDF
const pdfPositionLabels = {
  TABLE: "Item table column",
  RIGHT: "Right of a label",
  BELOW: "Below a label",
};

/**
 * Sales Invoice (PDF): a sample PDF's item table columns (with the first line's values) and text lines, to copy the column
 * headers and labels from, and a test of the saved mapping on it (nothing is imported).
 */
const PdfSampleHelper = ({ brandId, distributorId, onUseColumns }) => {
  const [file, setFile] = useState(null);
  const [layout, setLayout] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const params = { brandId, distributorId: distributorId || undefined };

  const readLayout = async (picked) => {
    setFile(picked);
    setPreview(null);
    if (!picked) return;
    setBusy(true);
    try {
      setLayout(await api.uploadFile("/api/v1/import-templates/pdf-layout", picked, params));
    } catch (error) {
      toast.error(error.message || "Could not read the PDF");
    } finally {
      setBusy(false);
    }
  };

  const testMapping = async () => {
    if (!file) return;
    setBusy(true);
    try {
      setPreview(await api.uploadFile("/api/v1/import-templates/pdf-preview", file, params));
    } catch (error) {
      setPreview([]);
      toast.error(error.message || "The saved mapping could not read the PDF");
    } finally {
      setBusy(false);
    }
  };

  const previewFields = preview && preview.length ? Array.from(new Set(preview.flatMap((row) => Object.keys(row.fields || {})))) : [];

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "12px", margin: "12px 0" }}>
      <h4 style={{ fontWeight: 700, marginBottom: "6px" }}>Sample PDF</h4>
      <div className="page-subtitle" style={{ marginBottom: "8px" }}>
        Pick one of this distributor's bill PDFs to see its item table columns and text. Map each table column header (as shown
        here — a header used twice is numbered, e.g. "Amt (2)") to a field as an "Item table column"; for bill header values (bill
        number, date, buyer) enter the label printed before or above the value. Save, then "Test saved mapping" to see the rows it
        reads.
      </div>
      <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
        <input type="file" accept=".pdf,application/pdf" onChange={(e) => readLayout(e.target.files?.[0] || null)} />
        <button type="button" className="secondary" onClick={testMapping} disabled={!file || busy}>
          {busy ? "Reading..." : "Test saved mapping"}
        </button>
      </div>
      {layout ? (
        <div style={{ marginTop: "10px" }}>
          <div className="page-subtitle">
            {layout.pages} page(s){layout.page ? `; item table found on page ${layout.page}` : "; no item table header found"}
          </div>
          {layout.columns?.length ? (
            <>
              <div className="table-wrapper" style={{ marginTop: "6px" }}>
                <table>
                  <thead>
                    <tr>
                      <th>Column Header</th>
                      <th>First Line's Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {layout.columns.map((column) => (
                      <tr key={column.label}>
                        <td>
                          <code>{column.label}</code>
                        </td>
                        <td>{column.sample || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" className="secondary" style={{ marginTop: "6px" }} onClick={() => onUseColumns(layout.columns.map((c) => c.label))}>
                Offer these headers in the mapping below
              </button>
            </>
          ) : null}
          <details style={{ marginTop: "8px" }}>
            <summary>Text lines of the page ({layout.lines?.length || 0})</summary>
            <pre style={{ maxHeight: "40vh", overflow: "auto", fontSize: "11px", background: "#f9fafb", padding: "8px" }}>
              {(layout.lines || []).join("\n")}
            </pre>
          </details>
        </div>
      ) : null}
      {preview ? (
        <div style={{ marginTop: "10px" }}>
          <div className="page-subtitle">
            {preview.length ? `First ${preview.length} line(s) read with the saved mapping:` : "No lines were read with the saved mapping."}
          </div>
          {preview.length ? (
            <div className="table-wrapper" style={{ marginTop: "6px", maxHeight: "40vh", overflow: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>From</th>
                    {previewFields.map((field) => (
                      <th key={field}>{field}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row) => (
                    <tr key={row.row}>
                      <td>{row.row}</td>
                      <td>{row.source}</td>
                      {previewFields.map((field) => (
                        <td key={field}>{row.fields?.[field] ?? ""}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const ImportColumnMapping = () => {
  const [documentTypes, setDocumentTypes] = useState({});
  const [brands, setBrands] = useState([]);
  const [templateKey, setTemplateKey] = useState("purchase-invoices");
  const [brandId, setBrandId] = useState("");
  const [headerByField, setHeaderByField] = useState({});
  const [requiredFields, setRequiredFields] = useState(new Set());
  // {field: {type: "STATIC" | "DERIVED", value}} — what a field takes when its column is missing or blank; none = column only
  const [sourceByField, setSourceByField] = useState({});
  const [extraFields, setExtraFields] = useState([]); // [{field, header}] — anything not in the canonical list
  const [isDefault, setIsDefault] = useState(true);
  // Sales Invoice (PDF): where each field's label is (TABLE when not set) and the text that ends the item table
  const [positionByField, setPositionByField] = useState({});
  const [tableEndText, setTableEndText] = useState("");
  const [pdfHeaders, setPdfHeaders] = useState([]);
  const isPdf = templateKey === PDF_KEY;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const canonicalFields = documentTypes[templateKey] || [];
  const canonicalFieldNames = useMemo(() => new Set(canonicalFields.map((f) => f.field)), [canonicalFields]);

  const loadDocumentTypes = async () => {
    try {
      const data = await api.get("/api/v1/import-templates/document-types");
      setDocumentTypes(data || {});
    } catch (error) {
      toast.error(error.message || "Failed to load document types");
    }
  };

  // the logged-in admin's distributor; templates are saved and read for it
  const distributorId = sessionStorage.getItem("distributorId");

  const loadBrands = async () => {
    try {
      // the backend returns only the logged-in admin's distributor's brand(s); with a single brand it is preselected
      const list = (await api.get("/api/v1/brands", { size: 200 }))?.content || [];
      setBrands(list);
      if (list.length === 1) setBrandId(String(list[0].id));
    } catch (error) {
      console.error("Failed to load brands", error);
      toast.error(error.message || "Failed to load brands");
    }
  };

  useEffect(() => {
    loadDocumentTypes();
    loadBrands();
  }, []);

  const loadTemplate = async () => {
    if (!brandId) {
      setHeaderByField({});
      setRequiredFields(new Set());
      setSourceByField({});
      setExtraFields([]);
      setIsDefault(true);
      setPositionByField({});
      setTableEndText("");
      return;
    }
    setLoading(true);
    try {
      const data = await api.get("/api/v1/import-templates", { templateKey, brandId, distributorId: distributorId || undefined });
      const canonical = {};
      const extras = [];
      Object.entries(data?.columnMapping || {}).forEach(([header, field]) => {
        if (canonicalFieldNames.has(field)) canonical[field] = header;
        else extras.push({ field, header });
      });
      setHeaderByField(canonical);
      setExtraFields(extras);
      setRequiredFields(new Set(data?.requiredColumns || []));
      setSourceByField(data?.fieldSources || {});
      setIsDefault(data?.isDefault ?? true);
      setPositionByField(data?.pdfSettings?.positions || {});
      setTableEndText(data?.pdfSettings?.tableEndText || "");
    } catch (error) {
      toast.error(error.message || "Failed to load mapping");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateKey, brandId]);

  const handleHeaderChange = (field, value) => {
    setHeaderByField((prev) => ({ ...prev, [field]: value }));
  };

  // "COLUMN" drops the field's source; a derived field starts on its first rule
  const handleSourceTypeChange = (f, type) => {
    setSourceByField((prev) => {
      const next = { ...prev };
      if (type === "COLUMN") delete next[f.field];
      else next[f.field] = { type, value: type === "DERIVED" ? f.derivedRules[0] : "" };
      return next;
    });
  };

  const handleSourceValueChange = (field, value) => {
    setSourceByField((prev) => ({ ...prev, [field]: { ...prev[field], value } }));
  };

  const toggleRequired = (field) => {
    setRequiredFields((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  };

  const addExtraField = () => {
    setExtraFields((prev) => [...prev, { field: "", header: "" }]);
  };

  const updateExtraField = (index, key, value) => {
    setExtraFields((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  };

  const removeExtraField = (index) => {
    setExtraFields((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const columnMapping = {};
    Object.entries(headerByField).forEach(([field, header]) => {
      if (header && header.trim()) columnMapping[header.trim()] = field;
    });
    for (const row of extraFields) {
      const field = row.field.trim();
      const header = row.header.trim();
      if (!field && !header) continue;
      if (!field || !header) {
        toast.error("Every extra field needs both a field name and the brand's column header");
        return;
      }
      if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(field)) {
        toast.error(`"${field}" isn't a valid field name — letters, digits, underscore only, starting with a letter`);
        return;
      }
      columnMapping[header] = field;
    }
    const fieldSources = {};
    for (const f of canonicalFields) {
      const source = sourceByField[f.field];
      if (!source) continue;
      const value = (source.value || "").trim();
      if (!value) {
        toast.error(`Enter the static value for ${f.field}`);
        return;
      }
      fieldSources[f.field] = { type: source.type, value };
    }
    if (Object.keys(columnMapping).length === 0) {
      toast.error("Map at least one column before saving");
      return;
    }
    setSaving(true);
    try {
      const pdfSettings = isPdf ? { positions: positionByField, tableEndText: tableEndText.trim() || null } : null;
      await api.put("/api/v1/import-templates", { columnMapping, requiredColumns: Array.from(requiredFields), fieldSources, pdfSettings },
        { templateKey, brandId, distributorId: distributorId || undefined });
      toast.success("Column mapping saved");
      loadTemplate();
    } catch (error) {
      toast.error(error.message || "Failed to save mapping");
    } finally {
      setSaving(false);
    }
  };

  const brandName = useMemo(() => brands.find((b) => String(b.id) === String(brandId))?.name, [brands, brandId]);

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading mapping...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Define how each brand's own Excel column headers map to our internal fields for imports. Column order never matters — only
        the header text you type below has to match the brand's file exactly. Columns mapped below "Extra Fields" don't need a real
        database column — they're captured and kept on the row regardless, so nothing from a brand's file is ever dropped. If a
        brand's file doesn't always carry a column, pick Static (one fixed value for every row) or Derived (worked out by the
        system) under "If Column Is Missing" — it is used whenever the column is absent or the cell is empty, so the upload no
        longer fails for it.
      </div>

      <div className="filters-bar">
        <select value={templateKey} onChange={(e) => setTemplateKey(e.target.value)}>
          {Object.keys(documentTypeLabels).map((key) => (
            <option key={key} value={key}>
              {documentTypeLabels[key]}
            </option>
          ))}
        </select>
        <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
          <option value="">Select brand</option>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>

      {!brandId ? (
        <div className="empty-state">
          <span className="empty-state-title">Select a brand to view or edit its column mapping</span>
        </div>
      ) : (
        <>
          <div className="page-subtitle" style={{ margin: "12px 0" }}>
            {isDefault
              ? `${brandName} has no custom mapping yet — using the generic default. Fill in headers below to override it.`
              : `Showing ${brandName}'s saved custom mapping.`}
          </div>

          {isPdf ? (
            <>
              <PdfSampleHelper brandId={brandId} distributorId={distributorId} onUseColumns={setPdfHeaders} />
              <div className="form-group" style={{ maxWidth: "420px", marginBottom: "12px" }}>
                <label>Item table ends at the line containing</label>
                <input value={tableEndText} onChange={(e) => setTableEndText(e.target.value)} placeholder="e.g. GSTSummary (blank = first gap)" />
              </div>
              <datalist id="pdf-column-headers">
                {pdfHeaders.map((header) => (
                  <option key={header} value={header} />
                ))}
              </datalist>
            </>
          ) : null}

          <h4 style={{ fontWeight: 700, marginBottom: "6px" }}>Known Fields</h4>
          <div className="table-wrapper" style={{ marginTop: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Internal Field</th>
                  <th>Required</th>
                  {isPdf ? <th>Where in the PDF</th> : null}
                  <th>{isPdf ? "Column Header / Label in the PDF" : "Brand's Column Header"}</th>
                  <th>If Column Is Missing</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {canonicalFields.map((f) => (
                  <tr key={f.field}>
                    <td>
                      <code>{f.field}</code>
                    </td>
                    <td>
                      <input type="checkbox" checked={requiredFields.has(f.field)} onChange={() => toggleRequired(f.field)} />
                    </td>
                    {isPdf ? (
                      <td>
                        <select
                          value={positionByField[f.field] || "TABLE"}
                          onChange={(e) =>
                            setPositionByField((prev) => {
                              const next = { ...prev };
                              if (e.target.value === "TABLE") delete next[f.field];
                              else next[f.field] = e.target.value;
                              return next;
                            })
                          }
                        >
                          {Object.entries(pdfPositionLabels).map(([value, text]) => (
                            <option key={value} value={value}>
                              {text}
                            </option>
                          ))}
                        </select>
                      </td>
                    ) : null}
                    <td>
                      <input
                        style={{ width: "100%" }}
                        value={headerByField[f.field] || ""}
                        onChange={(e) => handleHeaderChange(f.field, e.target.value)}
                        list={isPdf && (positionByField[f.field] || "TABLE") === "TABLE" ? "pdf-column-headers" : undefined}
                        placeholder={
                          isPdf
                            ? (positionByField[f.field] || "TABLE") === "TABLE"
                              ? "Item table column header, e.g. Rate% (2)"
                              : "Label printed before / above the value, e.g. Bill No:"
                            : `e.g. exact header this brand uses for ${f.field}`
                        }
                      />
                    </td>
                    <td>
                      <select
                        value={sourceByField[f.field]?.type || "COLUMN"}
                        onChange={(e) => handleSourceTypeChange(f, e.target.value)}
                        disabled={!f.staticAllowed && !(f.derivedRules || []).length}
                        title={!f.staticAllowed && !(f.derivedRules || []).length ? "This field identifies the row, so it must come from the file" : undefined}
                      >
                        <option value="COLUMN">None (column only)</option>
                        {f.staticAllowed ? <option value="STATIC">Static</option> : null}
                        {(f.derivedRules || []).length ? <option value="DERIVED">Derived</option> : null}
                      </select>
                    </td>
                    <td>
                      {sourceByField[f.field]?.type === "STATIC" ? (
                        <input
                          style={{ width: "100%" }}
                          value={sourceByField[f.field].value || ""}
                          onChange={(e) => handleSourceValueChange(f.field, e.target.value)}
                          placeholder="Value used for every row"
                        />
                      ) : sourceByField[f.field]?.type === "DERIVED" ? (
                        <select
                          style={{ width: "100%" }}
                          value={sourceByField[f.field].value || ""}
                          onChange={(e) => handleSourceValueChange(f.field, e.target.value)}
                        >
                          {f.derivedRules.map((rule) => (
                            <option key={rule} value={rule}>
                              {derivedRuleLabels[rule] || rule}
                            </option>
                          ))}
                        </select>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between" style={{ marginTop: "24px", marginBottom: "6px" }}>
            <h4 style={{ fontWeight: 700 }}>Extra Fields (no dedicated column — stored alongside the row)</h4>
            <button type="button" className="secondary" onClick={addExtraField} style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <Plus size={14} /> Add Field
            </button>
          </div>
          {extraFields.length === 0 ? (
            <div className="page-subtitle">No extra fields yet — add one for any column this brand sends that we don't have a dedicated field for (e.g. MRP, GRN Number).</div>
          ) : (
            <div className="table-wrapper" style={{ marginTop: 0 }}>
              <table>
                <thead>
                  <tr>
                    <th>Field Name (your choice)</th>
                    <th>Brand's Column Header</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {extraFields.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          style={{ width: "100%" }}
                          value={row.field}
                          onChange={(e) => updateExtraField(index, "field", e.target.value)}
                          placeholder="e.g. mrp"
                        />
                      </td>
                      <td>
                        <input
                          style={{ width: "100%" }}
                          value={row.header}
                          onChange={(e) => updateExtraField(index, "header", e.target.value)}
                          placeholder="e.g. MRP"
                        />
                      </td>
                      <td>
                        <button type="button" className="close-btn" onClick={() => removeExtraField(index)} title="Remove">
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="modal-actions" style={{ borderTop: "none", justifyContent: "flex-start" }}>
            <button className="primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Mapping"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ImportColumnMapping;
